import React from "react";
import { UtensilsCrossed, Croissant, Grape, Wine } from "lucide-react";
import Reveal from "../components/Reveal";
import PageHero from "../components/PageHero";
import { useReservation } from "../components/ReservationModal";
import { restaurant } from "../mock";

const iconMap = { UtensilsCrossed, Croissant, Grape, Wine };

const Restaurant = () => {
  const { openReservation } = useReservation();
  return (
    <div className="font-body">
      <PageHero
        image={restaurant.cover}
        eyebrow="Lezzet Durağı"
        title="Çelebi Restaurant"
        subtitle="Taze malzemeler, yöresel lezzetler ve zarif bir atmosfer."
      />

      {/* Intro */}
      <section className="py-24 md:py-28 bg-[#fdfbf7]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <p className="eyebrow text-[#b8893a] mb-4">Damak Zevkinize Hitap Eden Tatlar</p>
            <h2 className="font-display text-4xl md:text-5xl text-[#2b2620] font-500 leading-tight mb-6">
              Safranbolu Mutfağının <br /> Seçkin Lezzetleri
            </h2>
            <p className="text-[#6b6459] leading-[1.9] text-[15px] mb-6">{restaurant.intro}</p>
            <p className="text-[#6b6459] leading-[1.9] text-[15px]">
              Güne zengin açık büfe kahvaltımızla başlayın; akşamı ise yöresel
              lezzetlerimiz ve safranlı tatlılarımızla taçlandırın.
            </p>
          </Reveal>
          <Reveal delay={150}>
            <div className="img-zoom rounded-sm overflow-hidden shadow-2xl">
              <img
                src={restaurant.introImage}
                alt="Bakır tepsilerde sunulan yöresel Türk kahvaltısı ve mezeler"
                className="w-full h-[480px] object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-20 bg-[#f6f0e6]">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {restaurant.highlights.map((h, i) => {
              const Icon = iconMap[h.icon] || Wine;
              return (
                <Reveal key={i} delay={i * 100}>
                  <div className="text-center px-4">
                    <div className="w-16 h-16 mx-auto rounded-full bg-white flex items-center justify-center shadow-md mb-5">
                      <Icon size={26} className="text-[#b8893a]" />
                    </div>
                    <h3 className="font-display text-xl text-[#2b2620] font-600 mb-2">{h.title}</h3>
                    <p className="text-[13px] text-[#6b6459] leading-relaxed">{h.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Safranbolu signature dishes */}
      <section className="py-24 md:py-28 bg-[#fdfbf7]">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal className="text-center mb-14">
            <p className="eyebrow text-[#b8893a] mb-4">Yöreye Özgü</p>
            <h2 className="font-display text-4xl md:text-5xl text-[#2b2620] font-500 mb-5">
              Safranbolu'ya Özel Lezzetler
            </h2>
            <div className="gold-divider" />
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {restaurant.signatureDishes.map((d, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="showcase group" data-testid={`signature-dish-${i}`}>
                  <div className="showcase-img rounded-sm overflow-hidden h-64 mb-5 shadow-[0_18px_45px_-24px_rgba(0,0,0,0.5)]">
                    <img src={d.img} alt={d.alt} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <h3 className="font-display text-2xl text-[#2b2620] font-600 group-hover:text-[#b8893a] transition-colors">
                    {d.name}
                  </h3>
                  <p className="text-[13px] text-[#6b6459] mt-2 leading-relaxed">{d.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Menu */}
      <section className="py-24 md:py-28 bg-[#f6f0e6]">
        <div className="max-w-4xl mx-auto px-5 md:px-8">
          <Reveal className="text-center mb-14">
            <p className="eyebrow text-[#b8893a] mb-4">Seçki Menü</p>
            <h2 className="font-display text-4xl md:text-5xl text-[#2b2620] font-500 mb-5">İmza Lezzetlerimiz</h2>
            <div className="gold-divider" />
          </Reveal>
          <div className="space-y-1">
            {restaurant.menu.map((m, i) => (
              <Reveal key={i} delay={i * 60}>
                <div className="py-5 border-b border-[#ded2ba] group" data-testid={`menu-item-${i}`}>
                  <h3 className="font-display text-2xl text-[#2b2620] font-600 group-hover:text-[#b8893a] transition-colors">
                    {m.name}
                  </h3>
                  <p className="text-[13px] text-[#6b6459] mt-1.5 leading-relaxed">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="text-center text-[12px] text-[#9a9086] mt-8 italic">
            * Menümüz mevsime ve günün taze ürünlerine göre değişiklik gösterebilir.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#2b2620] text-center">
        <div className="max-w-2xl mx-auto px-5">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl text-white font-500 mb-6">
              Soframıza Buyurun
            </h2>
            <button
              onClick={() => openReservation()}
              data-testid="restaurant-reserve-btn"
              className="btn-gold px-9 py-4 rounded-sm"
            >
              Rezervasyon Yap
            </button>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Restaurant;
